import React, { useEffect, useMemo, useState } from 'react';

import { Table, TableBody, TableCell, TableFooter, TablePagination, TableRow, TableContainer } from '@material-ui/core';
import CreateGoalPanel from 'components/MeshList/goals/CreateGoalPanel';
import ObjectiveSidePanel from 'components/ObjectiveSidePanel';
import useLocalStorage from 'components/hooks/useLocalStorage';
import { useDispatch, useSelector } from 'react-redux';
import { useSelectedView } from 'reducers/viewsSlice';
import HeaderLabelIcons from 'screens/Team/TeamTasksListView/HeaderLabelIcons';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import styled, { css } from 'styled-components';
import { StyledBorderedPaper } from 'styles/common';
import { checkRabbitGoalsTasksFeatureFlagEnabled } from 'utils/helpers/featureFlags';

import { TEAM_TASKS_VIEW_TYPES } from 'constants/ProjectConstants';
import { localStorageKeys } from 'constants/localStorageKeys';
import { sendClickedOnCreateObjectiveEvent } from 'constants/mixpanelHelper';

import { getTabularData, fetchGoals } from 'services/analytics/goal';
import { showErrorSnackbar } from 'services/snackbar';

import { TableHeaderRow } from 'uiToolkitV1/TableComponents';
import { TablePagination as RabbitTablePagination } from 'uiToolkitV1/TableComponents/Pagination';
import { TableHead as RabbitTableHead, TableHeaderCell } from 'uiToolkitV1/TableComponents/TableHeader';

import { Loader, NoDataAvailable } from '../../commonComponents';
import { DISPLAY_COMPONENT_META } from '../../tableCellComponents';
import { useGoalAnalytics } from '../GoalAnalyticsContext';
import AddNewObjectiveOrGoalRow from './components/AddNewObjectiveOrGoalRow';
import NestedObjectiveTable from './components/NestedObjectiveTable';
import NoData from './components/NoData';
import { getIdsFromObjectRecursive } from './helpers/index';

const defaultRowsPerPage = 10;

const objectiveTableRowsPerPage = 'objectiveTableRowsPerPage';

export const defaultRowsPerPageForGoals = {
  [objectiveTableRowsPerPage]: defaultRowsPerPage,
};

const ObjectiveTableHeader = ({ headers, orderBy, setOrderBy }) =>
  useMemo(() => {
    const isSortable = (uuidType) => DISPLAY_COMPONENT_META[uuidType]?.isSortable;

    if (checkRabbitGoalsTasksFeatureFlagEnabled()) {
      return (
        <RabbitTableHead>
          <TableHeaderRow>
            {headers &&
              headers.map(({ id, uuidType, displayName, filterTagType }, index) => {
                const minWidth = '20rem';
                const width = index === 0 ? `calc(100% - ${headers.length - 1} * ${minWidth})` : `${minWidth}`;

                return (
                  <TableHeaderCell
                    key={`${id}`}
                    p="8px 12px"
                    width={width}
                    onClick={() => {
                      if (!isSortable(uuidType)) return;
                      if (orderBy.columnId === id) {
                        setOrderBy((prev) => ({
                          ...prev,
                          orderType: prev.orderType === 'ASC' ? 'DESC' : 'ASC',
                        }));
                      } else {
                        setOrderBy({
                          columnId: id,
                          orderType: 'ASC',
                          filterTagType,
                        });
                      }
                    }}
                    withSorting={isSortable(uuidType)}
                  >
                    {displayName}
                  </TableHeaderCell>
                );
              })}
          </TableHeaderRow>
        </RabbitTableHead>
      );
    }

    // return (
    //   <TableHead>
    //     <StyledTableRow>
    //       {headers &&
    //         headers.map(({ id, uuidType, displayName, filterTagType }, index) => {
    //           const minWidth = '20rem';
    //           const width = index === 0 ? `calc(100% - ${headers.length - 1} * ${minWidth})` : `${minWidth}`;

    //           return (
    //             <StyledTableCell
    //               key={`${id}`}
    //               // width={width}
    //               style={{ width }}
    //               size="small"
    //               onClick={() => {
    //                 if (!isSortable(uuidType)) return;
    //                 sendSortedGoalsEvent({
    //                   eventProperties: {
    //                     sortColumn: displayName,
    //                   },
    //                 });
    //                 if (orderBy.columnId === id) {
    //                   setOrderBy((prev) => ({
    //                     ...prev,
    //                     orderType: prev.orderType === 'ASC' ? 'DESC' : 'ASC',
    //                   }));
    //                 } else {
    //                   setOrderBy({
    //                     columnId: id,
    //                     orderType: 'ASC',
    //                     filterTagType,
    //                   });
    //                 }
    //               }}
    //             >
    //               <StyledBox
    //                 display="flex"
    //                 justifyContent={DISPLAY_COMPONENT_META[uuidType]?.align === 'center' ? 'center' : 'flex-start'}
    //               >
    //                 <StyledTypography
    //                   variant="caption"
    //                   color={theme.palette.text.label}
    //                   clickable={isSortable(uuidType)}
    //                 >
    //                   {displayName}
    //                 </StyledTypography>
    //                 {isSortable(uuidType) && (
    //                   <StyledHeaderLabelIcons
    //                     orderBy={orderBy.columnId}
    //                     headCell={{ id }}
    //                     order={String(orderBy.orderType).toLowerCase()}
    //                   />
    //                 )}
    //               </StyledBox>
    //             </StyledTableCell>
    //           );
    //         })}
    //     </StyledTableRow>
    //   </TableHead>
    // );
  }, [headers, orderBy, setOrderBy]);

/**
 * @deprecated 06-08-2024
 */
const ObjectiveTable = ({
  isTeamHubView = false,
  selectedTimeBoundPeriodsTeamHub,
  teamHubFilters,
  tagType,
  tagId,
  isOpenCreateObjectivePanel = false,
  setIsOpenCreateObjectivePanel = () => {},
  loadingTeamHub,
  activeFilters = [],
  refreshData,
  paginationProps = {},
  className = '',
  teamPageSelectedCohort = null,
}) => {
  const dispatch = useDispatch();

  const [rowsPerPageLS, setRowsPerPageLS] = useLocalStorage(
    localStorageKeys.analyticsGoalTableFilters,
    defaultRowsPerPageForGoals
  );

  const {
    data,
    dataCount,
    rowsPerPageOptions,
    rowsPerPage,
    pageNumber,
    setPageNumber,
    setRowsPerPage,
    setData,
    setAllData,
    updateRowDataModifiedForArray,
    addNewObjective,
  } = paginationProps;

  // const {
  //   data,
  //   dataCount,
  //   rowsPerPageOptions,
  //   rowsPerPage,
  //   pageNumber,
  //   setPageNumber,
  //   setRowsPerPage,
  //   setData,
  //   setAllData,
  //   updateRowDataModifiedForArray,
  //   addNewObjective
  // } = useObjectiveTablePagination({
  //   rowsPerPageOptions: [5, 10, 15],
  //   defaultRowsPerPage: rowsPerPageLS[objectiveTableRowsPerPage],
  //   initialPage: 0
  // });

  const {
    state: { currentState },
    setNoData,
    setErrorData,
  } = useGoalAnalytics();
  const { selectedCohort, selectedGroupByKey, selectedSplitByKey, selectedTimeBoundPeriods, noData } = currentState;

  const sideNavSelectedCohort = useSelectedView();

  const [loading, setLoading] = useState(true);
  const [headers, setHeaders] = useState(null);
  const [objectiveHeaderId, setObjectiveHeaderId] = useState(null);

  const [expandedRowIds, setExpandedRowIds] = useState({});

  const expandedRowIdsArray = getIdsFromObjectRecursive(expandedRowIds);

  const [orderBy, setOrderBy] = useState({ columnId: null, orderType: 'ASC', filterTagType: null });

  const fetchObjectiveData = (parentUuid) => {
    if (isTeamHubView) {
      return getTabularData({
        selectedCohort: teamPageSelectedCohort,
        groupByKeys: {
          uuid: null,
          keyName: 'objective',
          displayName: 'Objective',
          enabled: true,
        },
        splitByKeys: {
          uuid: null,
          keyName: 'none',
          displayName: 'None',
          enabled: true,
        },
        timeBounds: [selectedTimeBoundPeriodsTeamHub],
        parentUuid,
        orderBy,
        attributeFilter: {
          filterConditions: teamHubFilters,
        },
        isExpandedViewRequired: true,
      });
    }
    return getTabularData({
      groupByKeys: selectedGroupByKey,
      splitByKeys: selectedSplitByKey,
      timeBounds: selectedTimeBoundPeriods,
      parentUuid,
      orderBy,
      isExpandedViewRequired: true,
      cohort: selectedCohort,
    });
  };

  const fetchGoalsData = (parentUuid) =>
    fetchGoals({
      limit: 1000,
      offset: 0,
      cohort: selectedCohort,
      selectedCohort: sideNavSelectedCohort,
      timeBounds: isTeamHubView ? [selectedTimeBoundPeriodsTeamHub] : selectedTimeBoundPeriods,
      tagId: isTeamHubView ? tagId : parentUuid,
      resourceType: TEAM_TASKS_VIEW_TYPES.goalTree,
      filters: [...activeFilters],
      isTeamHubView,
      objectiveTimeboundFilter: {
        data: [parentUuid],
        operatorType: 'IN',
        type: 'TIMEBOUND_OBJECTIVE',
      },
      orderByWithFilterTagType: orderBy,
      isPrivateGoalRequired: !isTeamHubView,
    });

  const fetchData = async () => {
    try {
      setLoading(true);

      const res = await fetchObjectiveData();

      // converting the res from BE to a JS map
      const mappedResponse = res.values.map((rowObj) => ({
        dataObj: rowObj,
        level: 0,
        parentUuid: null,
        children: null,
        goals: null,
      }));

      setHeaders(res.columns);
      setAllData(mappedResponse);

      /* checking if res is empty
        if it's empty setting the value in context to show the
        no data component instead of the objective table
      */
      if (mappedResponse.length === 0) {
        setNoData(true);
      }
    } catch (err) {
      console.error('Error while fetching objective tabular data : ', err);
      setErrorData(true);
      dispatch(showErrorSnackbar(err.message || 'Failed to fetch Objectives!'));
    } finally {
      setLoading(false);
    }
  };

  const refetchData = async ({ pageNumber, onSuccess }) => {
    /* 
      • refetchData function is different from fetchData
      it has an onSuccess callback function and pageNumber argument,
      which helps with setting the correct pageNumber after user's modifications on ObjectiveTable

      • it also doesn't have a loading state setting and resetting to prevent loader rendering when refetching
      although a call has to be made on that.
    */

    try {
      setLoading(true);

      const res = await fetchObjectiveData();

      // converting the res from BE to a JS map
      const mappedResponse = res.values.map((rowObj) => ({
        dataObj: rowObj,
        level: 0,
        parentUuid: null,
        children: null,
        goals: null,
      }));

      setHeaders(res.columns);
      setAllData(mappedResponse);
      setPageNumber(pageNumber);

      /*      
       checking if there's a callback function, on success of api fetch, calling it
       mostly this onSuccess function is responsible for handling the modification
       of the state after any modification happens from the user on the table
       e.g. updating the progress of goal, changing the objective of goal etc 
      */
      if (onSuccess && typeof onSuccess === 'function') {
        onSuccess();
      }

      /* checking if res is empty
        if it's empty setting the value in context to show the
        no data component instead of the objective table
      */
      if (mappedResponse.length === 0) {
        setNoData(true);
      }
    } catch (err) {
      console.err('Error refetching objective tabular data : ', err);
      setErrorData(true);
      dispatch(showErrorSnackbar(err.message || 'Failed to fetch Objectives!'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if ((selectedCohort && selectedGroupByKey && selectedSplitByKey && selectedTimeBoundPeriods) || isTeamHubView) {
      fetchData();
    }
  }, [
    selectedCohort,
    selectedGroupByKey,
    selectedSplitByKey,
    selectedTimeBoundPeriods,
    isTeamHubView,
    JSON.stringify(teamHubFilters),
    tagId,
    tagType,
    JSON.stringify(selectedTimeBoundPeriodsTeamHub),
    refreshData,
  ]);

  useEffect(() => {
    if (orderBy.columnId !== null) fetchData();
  }, [orderBy]);

  useEffect(() => {
    if (!headers) return;

    const ObjectiveColumn = headers.filter((hObj) => hObj.uuidType === DISPLAY_COMPONENT_META.OBJECTIVE.id);

    if (ObjectiveColumn.length > 0) setObjectiveHeaderId(ObjectiveColumn[0].id);
  }, [headers]);

  // objective panel open/close states
  const [isObjectiveSidePanelOpen, setIsObjectiveSidePanel] = useState(false);

  // goal panel open/close states
  const [isOpenCreateGoalPanel, setIsOpenCreateGoalPanel] = useState(false);

  // timebound details of newly created objective

  const [timeboundDetailsOfCreatedObjective, setTimeboundDetailsOfCreatedObjective] = useState(null);

  const [isGoalPanelOfCreatedObjective, setIsGoalPanelOfCreatedObjective] = useState(false);

  const employee = useSelector(selectLoggedInEmployeeData);

  // is nested objective table KR panel

  const [isNestedObjectiveTableGoalPanel, setIsNestedObjectiveTableGoalPanel] = useState(false);
  const [timeboundDetailsNestedObjectiveTable, setTimeboundDetailsNestedObjectiveTable] = useState(null);
  const [afterGoalCreationNestedObjectiveTable, setAfterGoalCreationNestedObjectiveTable] = useState(() => {});

  if (loading || loadingTeamHub) {
    return <Loader />;
  }

  return (
    <>
      {isOpenCreateObjectivePanel && isTeamHubView && (
        <ObjectiveSidePanel
          open={isOpenCreateObjectivePanel}
          onCancel={() => {
            setTimeboundDetailsOfCreatedObjective(null);
            setIsGoalPanelOfCreatedObjective(false);
            setIsOpenCreateObjectivePanel(false);
          }}
          selectedTimeBoundPeriod={selectedTimeBoundPeriodsTeamHub}
          addNewObjective={addNewObjective}
          rows={data}
          updateRowDataModifiedForArray={updateRowDataModifiedForArray}
          isAlignmentAtParentLevel
          setIsOpenCreateGoalPanel={(value) => {
            setIsOpenCreateGoalPanel(value);
            setIsGoalPanelOfCreatedObjective(value);
          }}
          setTimeboundDetailsOfCreatedObjective={setTimeboundDetailsOfCreatedObjective}
          source={isTeamHubView ? 'Team Page' : 'Insights'}
        />
      )}
      {noData && !isTeamHubView ? (
        <>
          <NoDataAvailable
            showAddNewObjectiveOrGoalRow
            paginationProps={{
              data,
              dataCount,
              rowsPerPageOptions,
              rowsPerPage,
              pageNumber,
              setPageNumber,
              setRowsPerPage,
              setData,
              setAllData,
              updateRowDataModifiedForArray,
              addNewObjective,
            }}
            setIsOpenCreateGoalPanel={setIsOpenCreateGoalPanel}
            setIsGoalPanelOfCreatedObjective={setIsGoalPanelOfCreatedObjective}
            setTimeboundDetailsOfCreatedObjective={setTimeboundDetailsOfCreatedObjective}
          />
          {isOpenCreateGoalPanel ? (
            <CreateGoalPanel
              open={isOpenCreateGoalPanel}
              onCancel={() => {
                setIsOpenCreateGoalPanel(false);
                setIsNestedObjectiveTableGoalPanel(false);
              }}
              subObjective={isGoalPanelOfCreatedObjective ? { ...timeboundDetailsOfCreatedObjective } : null}
              employee={employee}
              selectedTimeBoundPeriod={selectedTimeBoundPeriods[0]}
              eventSource={isTeamHubView ? 'Team Page' : 'Insights Page'}
            />
          ) : null}
        </>
      ) : !data?.length ? (
        <NoData
          onAddKR={() => {
            setIsOpenCreateGoalPanel(true);
          }}
          onAddObjective={() => {
            sendClickedOnCreateObjectiveEvent({
              eventProperties: {
                source: isTeamHubView ? 'Team Page' : 'Insights Page',
              },
            });
            setIsOpenCreateObjectivePanel(true);
          }}
        />
      ) : (
        <>
          {!isTeamHubView && (
            <AddNewObjectiveOrGoalRow
              setIsOpenCreateObjectivePanel={setIsObjectiveSidePanel}
              setIsOpenCreateGoalPanel={setIsOpenCreateGoalPanel}
              isTeamHubView={isTeamHubView}
            />
          )}
          <StyledBorderedPaper className={className} overFlow="visible" marginBottom="2rem" padding="0" topGutter={2}>
            <StyledTableContainer style={{ overflow: 'visible' }}>
              <Table>
                <ObjectiveTableHeader headers={headers} orderBy={orderBy} setOrderBy={setOrderBy} />
                <TableBody>
                  {isObjectiveSidePanelOpen && (
                    <ObjectiveSidePanel
                      open={isObjectiveSidePanelOpen}
                      onCancel={() => setIsObjectiveSidePanel(false)}
                      uuid={null}
                      selectedTimeBoundPeriod={selectedTimeBoundPeriods[0]}
                      objectiveTimeBoundId={null}
                      addNewObjective={addNewObjective}
                      rows={data}
                      updateRowDataModifiedForArray={updateRowDataModifiedForArray}
                      isAlignmentAtParentLevel
                      setIsOpenCreateGoalPanel={(value) => {
                        setIsOpenCreateGoalPanel(value);
                        setIsGoalPanelOfCreatedObjective(value);
                      }}
                      setTimeboundDetailsOfCreatedObjective={setTimeboundDetailsOfCreatedObjective}
                      source={isTeamHubView ? 'Team Page' : 'Insights'}
                    />
                  )}
                  {isOpenCreateGoalPanel ? (
                    <CreateGoalPanel
                      open={isOpenCreateGoalPanel}
                      onCancel={() => {
                        setIsOpenCreateGoalPanel(false);
                        setIsNestedObjectiveTableGoalPanel(false);
                      }}
                      subObjective={
                        isNestedObjectiveTableGoalPanel
                          ? timeboundDetailsNestedObjectiveTable
                          : isGoalPanelOfCreatedObjective
                            ? { ...timeboundDetailsOfCreatedObjective }
                            : null
                      }
                      employee={employee}
                      selectedTimeBoundPeriod={selectedTimeBoundPeriods[0]}
                      afterGoalCreation={isNestedObjectiveTableGoalPanel ? () => refetchData({ pageNumber }) : () => {}}
                      eventSource={isTeamHubView ? 'Team Page' : 'Insights Page'}
                    />
                  ) : null}
                  <NestedObjectiveTable
                    rows={data}
                    headers={headers}
                    fetchObjectiveData={fetchObjectiveData}
                    fetchGoalsData={fetchGoalsData}
                    updateRowData={setData}
                    objectiveHeaderId={objectiveHeaderId}
                    levelsForWhichLinesToBeDrawn={[]}
                    heirarchy={[]}
                    refetchData={refetchData}
                    currentPageNumber={pageNumber}
                    setExpandedRowIds={setExpandedRowIds}
                    expandedRowIds={expandedRowIds}
                    rowsData={data}
                    setData={setData}
                    updateRowDataModifiedForArray={updateRowDataModifiedForArray}
                    addNewObjective={addNewObjective}
                    expandedRowIdsArray={expandedRowIdsArray}
                    selectedTimeBoundPeriodsTeamHub={selectedTimeBoundPeriodsTeamHub}
                    isTeamHubView={isTeamHubView}
                    loadingObjectiveTableData={loading}
                    setIsOpenCreateGoalPanelObjectiveTable={() => {
                      setIsOpenCreateGoalPanel(true);
                      setIsNestedObjectiveTableGoalPanel(true);
                    }}
                    setTimeboundDetailsNestedObjectiveTable={setTimeboundDetailsNestedObjectiveTable}
                    setAfterGoalCreationNestedObjectiveTable={setAfterGoalCreationNestedObjectiveTable}
                  />
                </TableBody>
                <TableFooter>
                  <StyledTableRow>
                    {checkRabbitGoalsTasksFeatureFlagEnabled() ? (
                      <RabbitTablePagination
                        count={dataCount}
                        page={pageNumber}
                        onPageChange={(_, pageNum) => {
                          setExpandedRowIds({});
                          setPageNumber(pageNum);
                        }}
                        rowsPerPage={rowsPerPage}
                        handleChangeRowsPerPage={({ target }) => {
                          setRowsPerPageLS({
                            ...rowsPerPageLS,
                            [objectiveTableRowsPerPage]: target.value,
                          });
                          setRowsPerPage(target.value);
                        }}
                        rowsPerPageOptions={rowsPerPageOptions}
                      />
                    ) : (
                      <TablePagination
                        rowsPerPageOptions={rowsPerPageOptions}
                        colSpan={headers && headers.length}
                        count={dataCount}
                        rowsPerPage={rowsPerPage}
                        page={pageNumber}
                        SelectProps={{
                          inputProps: { 'aria-label': 'rows per page' },
                        }}
                        onChangePage={(_, pageNum) => {
                          setExpandedRowIds({});
                          setPageNumber(pageNum);
                        }}
                        onChangeRowsPerPage={({ target }) => {
                          setRowsPerPageLS({
                            ...rowsPerPageLS,
                            [objectiveTableRowsPerPage]: target.value,
                          });
                          setRowsPerPage(target.value);
                        }}
                      />
                    )}
                  </StyledTableRow>
                </TableFooter>
              </Table>
            </StyledTableContainer>
          </StyledBorderedPaper>
        </>
      )}
    </>
  );
};

const StyledTableRow = styled(TableRow)`
  th,
  td {
    border-bottom: 1px solid ${(props) => props.theme.palette.borders.lightGrey};
  }
  ${(props) =>
    props.hasBottomBorder &&
    css`
      border-bottom: 1px solid ${props.theme.palette.borders.lightGrey};
    `}
`;

const StyledTableCell = styled(TableCell)`
  position: relative;
  border-left: ${(props) => props.borderLeft && `1px solid ${props.theme.palette.borders.lightGrey}`};
  cursor: ${(props) => (props.isSortable ? 'pointer' : 'default')};
  ${(props) =>
    props.hoverable &&
    css`
      &:hover {
        background-color: ${(props) => props.theme.palette.background.lightBlue};
      }
    `}
`;

const StyledHeaderLabelIcons = styled(HeaderLabelIcons)`
  margin-top: -4px;
  cursor: pointer;
`;

const StyledTableContainer = styled(TableContainer)`
  min-width: 1000px;
`;

export default ObjectiveTable;
